import type { Library } from '@googlemaps/js-api-loader'
import { Loader } from '@googlemaps/js-api-loader'

interface GoogleMapsPlugin {
  bindPlaces: (
    el: HTMLInputElement,
    onPlaceChanged: (address) => void,
    componentRestrictions: google.maps.places.ComponentRestrictions
  ) => Promise<void>
  restrictPlaces: (componentRestrictions: google.maps.places.ComponentRestrictions) => void
  unbindPlaces: () => void
  loadMapsAndMarker: () => Promise<Awaited<undefined | number>[]>
}

declare module '#app' {
  interface NuxtApp {
    $googleMaps: GoogleMapsPlugin
  }
}

declare module 'vue' {
  interface NuxtApp {
    $googleMaps: GoogleMapsPlugin
  }
}

export default defineNuxtPlugin(() => {
  const { configGoogleApi } = useFeatureFlags()

  let googleAutocomplete, googleAutocompleteListener
  const importedLibraries: Library[] = []

  const loader = new Loader({
    apiKey: configGoogleApi,
    version: 'weekly',
    language: useLocale(),
  })

  function importLibrary(library: Library) {
    if (importedLibraries.includes(library)) return
    return loader.importLibrary(library).then(() => importedLibraries.push(library))
  }

  function unbindPlaces() {
    if (!googleAutocomplete) return
    if (googleAutocompleteListener)
      window.google.maps.event.removeListener(googleAutocompleteListener)
    window.google.maps.event.clearInstanceListeners(googleAutocomplete)
    googleAutocomplete = undefined
    googleAutocompleteListener = undefined
  }

  async function bindPlaces(
    el: HTMLInputElement,
    onPlaceChanged: (address) => void,
    componentRestrictions: google.maps.places.ComponentRestrictions
  ) {
    await importLibrary('places')
    unbindPlaces()

    googleAutocomplete = new window.google.maps.places.Autocomplete(
      el,
      {
        componentRestrictions,
        types: ['address'],
        strictBounds: false,
        fields: ['address_components']
      }
    )

    const hidePacContainer = () => {
      setTimeout(() => {
        document.querySelectorAll<HTMLElement>('.pac-container').forEach((container) => {
          container.style.visibility = 'hidden'
        })
      }, 200)
    }

    const showPacContainer = () => {
      document.querySelectorAll<HTMLElement>('.pac-container').forEach((container) => {
        container.style.visibility = ''
      })
    }

    el.addEventListener('blur', hidePacContainer)
    el.addEventListener('focus', showPacContainer)

    googleAutocompleteListener = googleAutocomplete?.addListener('place_changed', () => {
      let addressLine1 = ''
      let addressLine2 = ''
      let postalCode = ''
      let city = ''
      let province = ''
      let street_number = ''
      let route = ''

      googleAutocomplete.getPlace().address_components.forEach((component) => {
        switch (component.types[0]) {
          case 'subpremise': {
            addressLine2 = component.long_name
            break
          }
          case 'street_number': {
            street_number = component.long_name
            break
          }
          case 'route': {
            route = component.short_name
            break
          }
          case 'postal_code': {
            postalCode = `${component.long_name}${postalCode}`
            break
          }
          case 'postal_code_suffix': {
            postalCode = `${postalCode}-${component.long_name}`
            break
          }
          case 'administrative_area_level_1': {
            province = component.short_name
            break
          }
          case 'locality':
          case 'sublocality_level_1':
          case 'postal_town':
          case 'administrative_area_level_3': {
            city = component.long_name
            break
          }
        }
      })
      addressLine1 = useAppConfig().components.googlePlaces.roadFirstCountries.includes(useCountryCode())
        ? `${route} ${street_number}`
        : `${street_number} ${route}`

      // While user selects address from google autocomplete list, input value displays long street name which is replaced with valued from addressLine1 after onPlaceChanged call.
      el.value = addressLine1
      onPlaceChanged?.({ addressLine1: addressLine1.trim(), addressLine2, postalCode, city, province })
    })
  }

  // change the restrictions after being initialized
  function restrictPlaces(componentRestrictions: google.maps.places.ComponentRestrictions) {
    if (googleAutocomplete)
      googleAutocomplete.setComponentRestrictions(componentRestrictions)
  }

  function loadMapsAndMarker() {
    return Promise.all([importLibrary('maps'), importLibrary('marker')])
  }

  return {
    provide: {
      googleMaps: {
        bindPlaces,
        restrictPlaces,
        unbindPlaces,
        loadMapsAndMarker,
      }
    }
  }
})
